import {
  Activity,
  Building2,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  LayoutDashboard,
  LogOut,
  Menu,
  Plug,
  Shield,
  SlidersHorizontal,
  User,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useLogout, useMe } from "@/lib/api";
import { cn } from "@/lib/utils";

type NavItem = {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
};

const WORKSPACE_NAV: NavItem[] = [
  { to: "/home", label: "Overview", icon: LayoutDashboard },
  { to: "/profile", label: "Profile", icon: User },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin/users", label: "Users", icon: Shield },
  { to: "/admin/activity", label: "Activity", icon: Activity },
  { to: "/admin/accounts", label: "Accounts", icon: Building2 },
  { to: "/admin/mcp", label: "MCP insights", icon: Plug },
];

const PORTAL_PERMISSIONS_NAV: NavItem = {
  to: "/portal/permissions",
  label: "Permissions",
  icon: SlidersHorizontal,
};
const PORTAL_APPROVALS_NAV: NavItem = {
  to: "/portal/approvals",
  label: "Approvals",
  icon: ClipboardCheck,
};

/**
 * Authenticated app frame: a collapsible desktop sidebar (which inverts to a
 * J&J-red rail when collapsed), a mobile drawer, and a topbar that names the
 * current route. Pages scroll inside <main>, never the document.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const logoutMutation = useLogout();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  // The shell never remounts across navigations, so close the mobile drawer on
  // any route change — including browser back/forward, which nav-click handlers
  // don't cover.
  useEffect(() => setMobileOpen(false), [pathname]);

  const isCustomer = me?.kind === "customer";
  const isAdmin = !isCustomer && me?.role === "admin";

  // Customers see only the portal sections their capabilities unlock; workforce
  // users keep the Workspace (+ Admin) nav.
  const customerNav: NavItem[] = [];
  if (isCustomer && me?.capabilities?.manage_account) {
    customerNav.push(PORTAL_PERMISSIONS_NAV);
  }
  if (isCustomer && me?.capabilities?.approve_orders) {
    customerNav.push(PORTAL_APPROVALS_NAV);
  }

  const handleLogout = async () => {
    await logoutMutation.mutateAsync();
    navigate("/login");
  };

  return (
    // h-screen + overflow-hidden bounds the frame to one viewport; the page
    // scrolls inside <main> so the scrollbar lives in the content column, not
    // beside the sidebar.
    <div className="bg-background flex h-screen overflow-hidden">
      <aside
        className={cn(
          "sticky top-0 hidden h-screen shrink-0 flex-col border-r transition-[width,background-color,color] duration-200 ease-out lg:flex",
          collapsed
            ? "bg-primary text-primary-foreground border-primary w-16"
            : "bg-card border-border w-64",
        )}
      >
        <SidebarContent
          isAdmin={isAdmin}
          isCustomer={isCustomer}
          customerNav={customerNav}
          userEmail={me?.email}
          onLogout={handleLogout}
          collapsed={collapsed}
          onToggleCollapsed={() => setCollapsed((prev) => !prev)}
        />
      </aside>

      {/* Mobile drawer — built on the Sheet (Radix Dialog) so it gets focus
          trap, Escape-to-close, scroll lock, and focus restore for free. */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          hideClose
          aria-describedby={undefined}
          className="bg-card w-72 gap-0 p-0"
        >
          <SheetTitle className="sr-only">Navigation menu</SheetTitle>
          <SidebarContent
            isAdmin={isAdmin}
            isCustomer={isCustomer}
            customerNav={customerNav}
            userEmail={me?.email}
            onLogout={handleLogout}
            onNavigate={() => setMobileOpen(false)}
            collapsed={false}
          />
        </SheetContent>
      </Sheet>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="border-border bg-card flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <p className="text-muted text-[10px] font-medium tracking-[0.18em] uppercase">
                MedTech · Customer Collaboration
              </p>
              <h1 className="font-display text-foreground truncate text-base font-medium tracking-tight sm:text-lg">
                {currentTitle(pathname)}
              </h1>
            </div>
          </div>
        </header>

        <main className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function SidebarContent({
  isAdmin,
  isCustomer,
  customerNav,
  userEmail,
  onLogout,
  onNavigate,
  collapsed,
  onToggleCollapsed,
}: {
  isAdmin: boolean;
  isCustomer: boolean;
  customerNav: NavItem[];
  userEmail?: string;
  onLogout: () => void;
  onNavigate?: () => void;
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}) {
  return (
    <>
      <div
        className={cn(
          "flex items-center border-b",
          collapsed
            ? "flex-col gap-1 border-white/15 px-2 py-3"
            : "border-border justify-between gap-2 px-5 py-4",
        )}
      >
        {collapsed ? (
          <>
            {/* On the red rail the transparent-background logo would vanish, so
                the compact mark is a white "J&J" wordmark instead. */}
            <NavLink
              to="/home"
              onClick={onNavigate}
              aria-label="Overview"
              className="font-display text-primary-foreground block py-1 text-base font-bold tracking-tight hover:opacity-80"
            >
              J&amp;J
            </NavLink>
            {onToggleCollapsed && (
              <button
                onClick={onToggleCollapsed}
                aria-label="Expand sidebar"
                className="text-primary-foreground flex h-7 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            )}
          </>
        ) : (
          <>
            <NavLink
              to="/home"
              onClick={onNavigate}
              aria-label="Overview"
              className="flex min-w-0 items-center gap-2.5 rounded-sm hover:opacity-80"
            >
              <img src="/brand/logo.png" alt="" className="h-7 w-7 shrink-0" />
              <span className="min-w-0">
                <span className="font-display text-foreground block text-sm leading-none font-semibold tracking-tight">
                  Johnson &amp; Johnson
                </span>
                <span className="text-muted mt-0.5 block text-[10px] tracking-[0.18em] uppercase">
                  MedTech
                </span>
              </span>
            </NavLink>
            {onNavigate ? (
              <button
                onClick={onNavigate}
                className="text-muted hover:text-foreground"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </button>
            ) : (
              onToggleCollapsed && (
                <button
                  onClick={onToggleCollapsed}
                  className="text-muted hover:bg-warm-gray-02 hover:text-foreground inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors"
                  aria-label="Collapse sidebar"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
              )
            )}
          </>
        )}
      </div>

      <nav className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
        {isCustomer ? (
          <>
            {!collapsed && <SectionLabel>Portal</SectionLabel>}
            <ul className="space-y-0.5">
              {customerNav.map((item) => (
                <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </ul>
          </>
        ) : (
          <>
            {!collapsed && <SectionLabel>Workspace</SectionLabel>}
            <ul className="space-y-0.5">
              {WORKSPACE_NAV.map((item) => (
                <NavRow key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
              ))}
            </ul>

            {isAdmin && (
              <>
                {!collapsed ? (
                  <SectionLabel className="pt-6">Admin</SectionLabel>
                ) : (
                  <div className="my-3 border-t border-white/15" aria-hidden />
                )}
                <ul className="space-y-0.5">
                  {ADMIN_NAV.map((item) => (
                    <NavRow
                      key={item.to}
                      item={item}
                      collapsed={collapsed}
                      onNavigate={onNavigate}
                    />
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </nav>

      {collapsed ? (
        <div className="space-y-1 border-t border-white/15 px-2 py-3">
          <button
            onClick={onLogout}
            aria-label="Sign out"
            className="text-primary-foreground flex h-10 w-full items-center justify-center rounded-md transition-colors hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div className="border-border space-y-1 border-t px-3 py-4">
          {userEmail && (
            <p className="text-muted truncate px-3 pb-1 text-xs" title={userEmail}>
              {userEmail}
            </p>
          )}
          <button
            onClick={onLogout}
            className="text-foreground hover:bg-warm-gray-02 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </>
  );
}

function SectionLabel({ children, className }: { children: string; className?: string }) {
  return (
    <p
      className={cn(
        "text-muted px-2 pb-2 text-[10px] font-medium tracking-[0.18em] uppercase",
        className,
      )}
    >
      {children}
    </p>
  );
}

function NavRow({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <li>
      <NavLink
        to={item.to}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        aria-label={collapsed ? item.label : undefined}
        className={({ isActive }) =>
          cn(
            "group flex items-center rounded-md transition-colors",
            collapsed ? "h-10 w-full justify-center" : "gap-2.5 px-3 py-2 text-sm",
            collapsed
              ? isActive
                ? "text-primary bg-white"
                : "text-primary-foreground hover:bg-white/10"
              : isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground hover:bg-warm-gray-02",
          )
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </NavLink>
    </li>
  );
}

function currentTitle(pathname: string): string {
  if (pathname.startsWith("/portal/permissions")) {
    return "Permissions";
  }
  if (pathname.startsWith("/portal/approvals")) {
    return "Order approvals";
  }
  if (pathname.startsWith("/portal")) {
    return "Customer portal";
  }
  if (pathname.startsWith("/admin/users")) {
    return "User management";
  }
  if (pathname.startsWith("/admin/activity")) {
    return "Activity";
  }
  if (pathname.startsWith("/admin/mcp")) {
    return "MCP insights";
  }
  if (pathname.startsWith("/admin/accounts/")) {
    return "Customer account";
  }
  if (pathname.startsWith("/admin/accounts")) {
    return "Customer accounts";
  }
  if (pathname.startsWith("/profile")) {
    return "Profile";
  }
  if (pathname.startsWith("/home")) {
    return "Overview";
  }
  return "Workspace";
}
